import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Anchor, Skull, Swords, ArrowDown, MapPin, Heart, Car, Wine, UtensilsCrossed, Cake, Music } from "lucide-react";
import { WEDDING, IMAGES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import StickyNav from "@/components/StickyNav";
import InfoUtili from "@/components/InfoUtili";
import RSVPForm from "@/components/RSVPForm";
import Footer from "@/components/Footer";
import EditableText from "@/components/EditableText";
import EditableImage from "@/components/EditableImage";

const WANTED_IMAGE = "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/zc2j5oba_onepiece_final_def.webp";
const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/t8gtbx9f_video-final-vdef.mp4";
const STATIC_IMAGE_AFTER_VIDEO = "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/ei782zjl_ungiorno.jpg";
const STATIC_IMAGE_MOBILE = "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/dkdd1m1t_storia-hero-mobile.jpg";

// Full timeline for STORIA page (from CSV)
const STORY_TIMELINE = [
  { ora: "15:30", titolo: "Arrivo ospiti", desc: "Ricerca parcheggio (ahah, buona fortuna!) nei due punti che ti indicheremo, accomodati in piazza e goditi il panorama mentre ci prepariamo ad arrivare", icon: "MapPin" },
  { ora: "16:00", titolo: "Cerimonia", desc: "Le sedie buone vanno a ruba per il momento serio (o quasi). Anche se la sposa non garantisce puntualità, conviene arrivare per tempo. Mamma, prepara i fazzoletti!", icon: "Heart" },
  { ora: "17:30", titolo: "Trasferimento", desc: "Se tutto va bene a quest'ora saremo (lo diciamo? Lo diciamo!) Moglie & Marito, ma soprattutto saremo pronti a festeggiare con le persone più care che abbiamo", icon: "Car" },
  { ora: "18:00", titolo: "Ricevimento", desc: "Ci spostiamo alla Fortezza, una fortezza medioevale sul mare con tanto di galere per gli ospiti più tranquilli. Sì esatto, ospiti tranquilli non ne vogliamo! E' il momento di dare sfogo alla fame e alla sete più becera", icon: "Wine" },
  { ora: "20:00", titolo: "Cena", desc: "Finalmente ci sediamo a tavola, già pieni e con il grande rimpianto di non aver comprato il vestito di (almeno) una taglia in più. Ma tanto che ce frega, abbiamo tutto domani per digiunare", icon: "UtensilsCrossed" },
  { ora: "22:00", titolo: "Taglio Torta", desc: "Come in ogni pasto che si rispetti, c'è un momento speciale chiamato dolcino. Sarà un momento emozionante e iperglicemico.", icon: "Cake" },
  { ora: "23:00", titolo: "Afterparty", desc: "Open bar e si balla fino a quando vogliono le gambe... o i Carabinieri. Al massimo invitiamo anche loro, tanto siamo (quasi) tutti terroni", icon: "Music" },
];

const iconMap = { MapPin, Heart, Car, Wine, UtensilsCrossed, Cake, Music };

// Piccoli Aiutanti for STORIA - 6 people
const PICCOLI_AIUTANTI_STORIA = [
  {
    nome: "Rosita Sorbara",
    ruolo: "Testimone della sposa",
    desc: "Amica dai tempi in cui all'oratorio facevano finta di essere le mogli di Cannavaro e Sir Ulrich Von Liechtenstein. Passate per una notevole ossessione per i fratelli Salvatore, sono oggi sorelle di fatto. Ladies & Gentlemen Rosita Luceeee",
    foto: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/60rjb8iv_rosita.jpeg"
  },
  {
    nome: "Stefano Buscemi",
    ruolo: "Testimone dello sposo",
    desc: "Al fianco di Stefano dai tempi dell'asilo. Una bromance lunga una vita a cui sono legati ricordi indelebili e una spalla indispensabile nei momenti più difficili. Ladies & Gentlemen, Busci, Ausch, Buscino che labbra morbide che hai",
    foto: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/u260clxv_busci.jpg"
  },
  {
    nome: "Viola Meroni",
    ruolo: "Testimone della sposa",
    desc: "Dietro l'ingegnere tutta d'un pezzo, esempio di ordine e rigore, c'e' un cuore di irresistibile scioglievolezza, una fidata amica con cui piangere, ridere e bere un sacco di vino buono! Ladies & Gentlemen, Violameroooooni",
    foto: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/jopnqzkw_viola.jpeg"
  },
  {
    nome: "Matteo Orsini",
    ruolo: "Testimone dello sposo",
    desc: "Piu' di mezza vita fianco a fianco, compagno instancabile di musica, di calcio, di vacanze e di vita. Anima e core piu' di quanto non lo faccia vedere. Ladies & Gentlemen, Orso, Orsacchio, Orzata, El Campeon del Fanta 2026",
    foto: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/cov212ee_orso.jpeg"
  },
  {
    nome: "Andrea Golia",
    ruolo: "Ufficiale di Stato Civile",
    desc: "Per molti semplicemente \"Il Mister\". Allenatore professionista, urlatore professionista, socio e testimone dal giorno zero di questa storia d'amore. Un pezzo di cuore a cui non potevamo ne' volevamo rinunciare. Ladies & Gentlemen: Goliiiiiiiis",
    foto: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/pf40b009_goli.jpeg"
  },
  {
    nome: "TU!",
    ruolo: "Parte del nostro cuore",
    desc: "Stiamo organizzando il matrimonio in meno di 6 mesi, percio' gia' lo sai: sara' indispensabile aiuto e comprensione per divertirci a cuor leggero e gestire con un po' di pazienza le cose che non saranno perfette. Tanto hai gia' l'abitudine visto che ci frequentiamo da mo'...",
    foto: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/8xakvpia_ziosam.jpg"
  },
];

export default function StoryFlow() {
  const [twistRevealed, setTwistRevealed] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const videoRef = useRef(null);

  // Set page title
  useEffect(() => {
    document.title = "Lelia & Stefano si sposano";
  }, []);

  // Flag to track if we've already set up the video
  const hasSetupVideo = useRef(false);

  // Handle video playback - cleanup on unmount
  useEffect(() => {
    const video = videoRef.current;
    if (video && !hasSetupVideo.current) {
      hasSetupVideo.current = true;
      
      // Set volume
      video.volume = 0.5;
      
      // The video starts muted via HTML attribute (required for autoplay)
      // We try to unmute and play with sound - if it fails, we keep it muted
      const tryUnmuteAndPlay = async () => {
        try {
          video.muted = false;
          await video.play();
          // Success! Video is playing with sound
        } catch (e) {
          // Failed to play with sound - restore muted state and let autoplay work
          video.muted = true;
          video.play().catch(() => {});
        }
      };
      
      // Small delay to let the muted autoplay start first
      setTimeout(tryUnmuteAndPlay, 100);
    }
    
    // Cleanup: stop video and audio when leaving the page
    return () => {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    };
  }, []);

  const handleVideoEnd = () => {
    setVideoEnded(true);
  };

  const toggleTwist = () => {
    setTwistRevealed(!twistRevealed);
  };

  return (
    <div className="bg-w-cream min-h-screen" data-testid="story-flow">
      <StickyNav />

      {/* === HERO SECTION - SPLIT LAYOUT === */}
      <section id="home" className="relative min-h-screen overflow-hidden bg-black">
        {/* Desktop: Split layout - Text left, Video right */}
        {/* Mobile: Video on top, text overlapping bottom */}
        
        {/* Video - positioned on right half on desktop, full on mobile */}
        <div className="absolute inset-0 md:left-1/2 md:right-0">
          {!videoEnded ? (
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              onEnded={handleVideoEnd}
              className="w-full h-full object-cover"
              data-testid="hero-video"
            >
              <source src={HERO_VIDEO} type="video/mp4" />
            </video>
          ) : (
            <>
              {/* Desktop: original image with left fade gradient */}
              <div className="hidden md:block w-full h-full relative">
                <img 
                  src={STATIC_IMAGE_AFTER_VIDEO} 
                  alt="Lelia e Stefano" 
                  className="w-full h-full object-cover"
                  data-testid="hero-static-image-desktop"
                />
                {/* Left fade gradient - 15% of width */}
                <div className="absolute inset-y-0 left-0 w-[15%] bg-gradient-to-r from-black to-transparent" />
              </div>
              {/* Mobile: alternative centered image */}
              <img 
                src={STATIC_IMAGE_MOBILE} 
                alt="Lelia e Stefano" 
                className="md:hidden w-full h-full object-cover"
                data-testid="hero-static-image-mobile"
              />
            </>
          )}
          {/* Gradient overlay for mobile text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent md:bg-gradient-to-r md:from-black/60 md:via-transparent md:to-transparent" />
        </div>

        {/* Desktop: Dark left half background */}
        <div className="hidden md:block absolute inset-y-0 left-0 w-1/2 bg-black" />

        {/* Content */}
        <div className="relative z-10 min-h-screen flex flex-col justify-end md:justify-center px-6 md:px-12 lg:px-20 pb-12 md:pb-0">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="max-w-xl"
          >
            {/* Main Title */}
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white font-bold leading-none tracking-tight drop-shadow-2xl">
              Lelia <span className="font-accent italic text-w-gold">&</span> Stefano
            </h1>

            {/* Subtitle */}
            <p className="font-body text-base md:text-lg text-white/90 mt-4 tracking-wide">
              Guarda subito tutta la stagione
            </p>

            {/* Description */}
            <p className="font-body text-sm md:text-base text-white/70 mt-4 leading-relaxed max-w-lg">
              Una storia d'amore tra due rampolli dell'Alta Societa' della periferia milanese e la loro sfacciata fortuna di essersi incontrati, tra 8 miliardi di persone.<br /><br />
              <span className="italic text-white/50">Attenzione: i fatti e i riferimenti a cose o persone sono frutto della Deficienza Artificiale.</span>
            </p>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
            className="absolute bottom-4 left-1/2 -translate-x-1/2"
          >
            <ArrowDown className="w-5 h-5 text-white/50 animate-bounce" />
          </motion.div>
        </div>
      </section>

      {/* === OUR STORY TEXT === */}
      <section className="py-8 md:py-12 bg-w-cream">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto text-center px-6"
        >
          <p className="font-body text-sm md:text-base text-w-muted leading-relaxed">
            Colleghi, poi amici, poi una svolta.<br />
            Come un incendio incontrollabile che ha travolto le nostre vite, il primo bacio, una vacanza insieme, poi il Covid.<br />
            "Tutta la mia famiglia ha il Covid". "Vieni da me per queste due settimane?" e poi "Non andartene", finche' quel <em className="font-accent text-w-charcoal">Resta. Si', resto!</em> e' diventato un Restiamo l'uno al fianco dell'altra fino ad invecchiare, insieme.<br />
            Con la piu' sincera speranza nel cuore che sia per sempre.
          </p>
        </motion.div>
      </section>

      {/* === INTERMEZZO: THE TRIGGER === */}
      <section className="py-4 md:py-6 text-center bg-w-cream">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-xl mx-auto px-6"
        >
          <p className="font-accent italic text-2xl md:text-3xl text-w-charcoal leading-relaxed">
            "Ma come in ogni <span className="line-through">bella</span> <strong>vera</strong> storia che si rispetti, non e' tutto oro cio' che luccica."
          </p>
          <Button
            data-testid="twist-trigger-btn"
            onClick={toggleTwist}
            className="mt-8 mb-[10px] bg-w-charcoal text-w-cream hover:bg-w-dark font-body uppercase tracking-widest text-xs px-8 py-6 rounded-full btn-wedding"
          >
            {twistRevealed ? "Nascondi la verita'" : "Scopri la vera verita'"}
          </Button>
        </motion.div>
      </section>

      {/* === ACT II: THE TWIST === */}
      <AnimatePresence>
        {twistRevealed && (
          <motion.section
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.8 }}
            className="overflow-hidden"
          >
            {/* Dark PLOT TWIST Section */}
            <div className="bg-w-dark py-10 md:py-14 overflow-hidden relative">
              <div className="absolute inset-0 opacity-10">
                <img src={IMAGES.guitar} className="w-full h-full object-cover" alt="" />
              </div>
              <div className="relative">
                <div className="text-center px-6">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    className="flex items-center justify-center gap-4 mb-6"
                  >
                    <Swords className="w-6 h-6 text-w-gold" strokeWidth={1.5} />
                    <Skull className="w-8 h-8 text-w-cream twist-glitch" strokeWidth={1.5} />
                    <Anchor className="w-6 h-6 text-w-gold" strokeWidth={1.5} />
                  </motion.div>
                  <h2 className="font-mono-twist uppercase text-2xl md:text-4xl text-w-cream tracking-wider easter-egg" data-secret="...forse ci saranno cannoni.">
                    Plot twist.
                  </h2>
                </div>
              </div>
            </div>

            {/* Wanted Image - NEW onepiece_final_def - aligned with portraits below */}
            <div className="bg-w-cream py-6 md:py-8">
              <div className="max-w-3xl mx-auto px-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  data-testid="twist-image-block"
                >
                  <EditableImage id="twist-main-image" defaultSrc={WANTED_IMAGE} alt="Wanted Posters" className="w-full" />
                </motion.div>
              </div>
            </div>

            {/* Sposa & Sposo portraits */}
            <div className="bg-w-cream py-4 md:py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 max-w-3xl mx-auto px-6">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <EditableImage id="portrait-lelia" defaultSrc="https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/q3sqe1se_bozzolo.jpg" alt="Lelia" className="aspect-[3/4] bg-w-sand/40 border border-w-sand overflow-hidden" />
                  <h3 className="font-heading text-2xl text-w-charcoal mt-4">Lelia</h3>
                  <p className="font-body text-sm text-w-muted mt-2 leading-relaxed max-w-xs mx-auto">
                    Lentezza è il suo secondo nome, si dice sia stata lei a inventare il concetto di slow life. Vedere qualsiasi cosa sul divano insieme a lei dopo le 21 pare sia impossibile. Abile manipolatrice, maniaca del controllo, disordinata per vocazione…
                  </p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <EditableImage id="portrait-stefano" defaultSrc="https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/7xho5qvg_sapio.jpg" alt="Stefano" className="aspect-[3/4] bg-w-sand/40 border border-w-sand overflow-hidden" />
                  <h3 className="font-heading text-2xl text-w-charcoal mt-4">Stefano</h3>
                  <p className="font-body text-sm text-w-muted mt-2 leading-relaxed max-w-xs mx-auto">
                    Pignolo, rompipalle fino allo sfinimento, dormiglione professionista, permaloso, pollice nero incubo di ogni pianta e non la smette un attimo di sfiancare la povera Lelia, specie quando cucina. Ah, non sta mai fermo un attimo. Però ha anche dei difetti…
                  </p>
                </motion.div>
              </div>
              {/* Closing statement */}
              <div className="pt-[50px] max-w-3xl mx-auto px-6">
                <p className="font-accent italic text-xl md:text-2xl text-w-charcoal text-center">
                  Nonostante questo, si scelgono ogni giorno.
                </p>
                <p className="font-accent italic text-base md:text-lg text-w-charcoal text-center mt-2">
                  Con tanto di benedizione di Nonna Giovanna.
                </p>
                {/* Audio player for Nonna Giovanna */}
                <div className="flex flex-col items-center mt-4">
                  <button 
                    onClick={() => {
                      const audio = document.getElementById('nonna-audio');
                      if (audio.paused) {
                        audio.play();
                      } else {
                        audio.pause();
                      }
                    }}
                    className="w-12 h-12 rounded-full bg-w-sage/20 hover:bg-w-sage/30 flex items-center justify-center transition-colors"
                    data-testid="nonna-audio-btn"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-w-sage">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                    </svg>
                  </button>
                  <audio id="nonna-audio" src="https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/yat4m8tj_nonnagiovanna.mp4" preload="metadata" />
                </div>
                <div className="h-[30px]" />
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* === TIMELINE (Full version for STORIA) === */}
      <section id="giorno" className="section-padding bg-w-sand/30" data-testid="timeline-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="font-body text-xs tracking-[0.3em] uppercase text-w-sage">Il programma</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-w-charcoal mt-2">Il Grande Giorno</h2>
            <p className="font-body text-w-muted mt-3 text-sm md:text-base max-w-md">
              Venerdi' 18 Settembre 2026. Un giorno che non dimenticherete. Neanche noi, speriamo.
            </p>
          </motion.div>

          <div className="relative">
            <div className="timeline-line" />

            {STORY_TIMELINE.map((event, i) => {
              const Icon = iconMap[event.icon] || Heart;
              const isLeft = i % 2 === 0;

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className={`relative flex items-start mb-12 md:mb-16 ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  } flex-row`}
                  data-testid={`timeline-event-${i}`}
                >
                  <div className="absolute left-[24px] md:left-1/2 w-3 h-3 bg-w-sage rounded-full -translate-x-1/2 mt-2 z-10 ring-4 ring-w-cream" />

                  <div className={`ml-12 md:ml-0 md:w-[45%] ${
                    isLeft ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'
                  }`}>
                    <div className={`inline-flex items-center gap-2 mb-2 ${isLeft ? 'md:flex-row-reverse' : ''}`}>
                      <Icon className="w-4 h-4 text-w-sage" strokeWidth={1.5} />
                      <span className="font-mono-twist text-lg md:text-xl text-w-charcoal font-bold tracking-wider">{event.ora}</span>
                    </div>
                    <h3 className="font-heading text-xl md:text-2xl text-w-charcoal">{event.titolo}</h3>
                    <p className="font-body text-sm text-w-muted mt-1">{event.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Piccoli Aiutanti - 6 people */}
      <section className="section-padding pb-2 md:pb-3 bg-w-cream" data-testid="piccoli-aiutanti">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
            <p className="font-body text-xs tracking-[0.3em] uppercase text-w-sage">Il dream team</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-w-charcoal mt-2">Piccoli Aiutanti</h2>
            <p className="font-body text-w-muted mt-3 text-sm md:text-base max-w-lg">
              Le persone che saranno in prima fila per darci una mano. Una mano operativa, una mano emotiva. Una mano da una vita insomma.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {PICCOLI_AIUTANTI_STORIA.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center" data-testid={`aiutante-${i}`}>
                <EditableImage
                  id={`aiutante-foto-storia-${i}`}
                  defaultSrc={p.foto}
                  alt={p.nome}
                  className="aspect-square bg-w-sand/30 border border-w-sand overflow-hidden mx-auto max-w-[200px]"
                />
                <h3 className="font-heading text-lg text-w-charcoal mt-4">{p.nome}</h3>
                <p className="font-body text-xs text-w-sage uppercase tracking-wider">{p.ruolo}</p>
                <p className="font-body text-sm text-w-muted mt-2 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <InfoUtili />
      <RSVPForm />
      <Footer />
    </div>
  );
}
